"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.zIndices = exports.reservedRowKinds = exports.EXPANDABLE_HEADER = exports.TOTALS = exports.HEADER = exports.matchesFilter = exports.maybeApplyFunction = exports.getJustification = exports.getAlignment = exports.getFirstOrLastCellCss = exports.getIndentationCss = exports.emptyCell = exports.DESC = exports.ASC = exports.applyRowFn = exports.isGridCellContent = exports.toContent = void 0;
const jsx_runtime_1 = require("@emotion/react/jsx-runtime");
const ExpandableHeader_1 = require("../components/ExpandableHeader");
const SortHeader_1 = require("../components/SortHeader");
const Css_1 = require("../../../Css");
const getInteractiveElement_1 = require("../../../utils/getInteractiveElement");
/** If a column def return just string text for a given row, apply some default styling. */
function toContent(maybeContent, isHeader, canSortColumn, isClientSideSorting, style, as, alignment, column, isExpandableHeader, isExpandable, minStickyLeftOffset) {
    var _a, _b, _c;
    let content = isGridCellContent(maybeContent) ? maybeContent.content : maybeContent;
    if (typeof content === "function") {
        // Actually create the JSX by calling `content()` here (which should be as late as
        // possible, i.e. only for visible rows if we're in a virtual table).
        content = content();
    }
    else if (as === "virtual" && canSortColumn && isClientSideSorting && isJSX(content)) {
        // When using client-side sorting, we call `applyRowFn` not only during rendering, but
        // up-front against all rows (for the currently sorted column) to determine their
        // sort values.
        //
        // Pedantically this means that any table using client-side sorting should not
        // build JSX directly in its GridColumn functions, but this overhead is especially
        // noticeable for large/virtualized tables, so we only enforce using functions
        // for those tables.
        throw new Error("GridTables with as=virtual & sortable columns should use functions that return JSX, instead of JSX");
    }
    content =
        isGridCellContent(maybeContent) && !!maybeContent.onClick
            ? (0, getInteractiveElement_1.getButtonOrLink)(content, maybeContent.onClick, {
                css: Css_1.Css.maxw100.lightBlue700.ta("inherit").if(((_a = style === null || style === void 0 ? void 0 : style.presentationSettings) === null || _a === void 0 ? void 0 : _a.wrap) === false).truncate.$,
            })
            : content;
    if (content && typeof content === "string" && isHeader && canSortColumn) {
        return ((0, jsx_runtime_1.jsx)(SortHeader_1.SortHeader, { content: content, iconOnLeft: alignment === "right", sortKey: (_b = column.serverSideSortKey) !== null && _b !== void 0 ? _b : column.id }));
    }
    else if (content && typeof content === "string" && isExpandableHeader && isExpandable) {
        return (0, jsx_runtime_1.jsx)(ExpandableHeader_1.ExpandableHeader, { title: content, column: column, minStickyLeftOffset: minStickyLeftOffset, as: as });
    }
    else if (content && typeof content === "string" && isExpandableHeader) {
        return (0, jsx_runtime_1.jsx)("span", { css: Css_1.Css.lineClamp2.$, children: content });
    }
    else if (!isContentEmpty(content) && isHeader && typeof content === "string") {
        return ((0, jsx_runtime_1.jsx)("span", { css: Css_1.Css.lineClamp2.$, title: content, children: content }));
    }
    else if (!isHeader && content && ((_c = style === null || style === void 0 ? void 0 : style.presentationSettings) === null || _c === void 0 ? void 0 : _c.wrap) === false && typeof content === "string") {
        // In order to truncate the text properly, then we need to wrap it in another element
        // as our cell element is a flex container, which don't allow for applying truncation styles directly on it.
        return ((0, jsx_runtime_1.jsx)("span", { css: Css_1.Css.truncate.mw0.$, title: content, children: content }));
    }
    else if (style.emptyCell && isContentEmpty(content)) {
        // If the content is empty and the user specified an `emptyCell` node, return that.
        return style.emptyCell;
    }
    return content;
}
exports.toContent = toContent;
function isGridCellContent(content) {
    return typeof content === "object" && !!content && "content" in content;
}
exports.isGridCellContent = isGridCellContent;
const emptyValues = ["", null, undefined];
function isContentEmpty(content) {
    return emptyValues.includes(content);
}
/** Return the content for a given column def applied to a given row. */
function applyRowFn(column, row, api, level, expanded) {
    // Usually this is a function to apply against the row, but sometimes it's a hard-coded value, i.e. for headers
    const maybeContent = column[row.kind];
    if (typeof maybeContent === "function") {
        // Auto-destructure data
        return maybeContent(row["data"], { row: row, api, level, expanded });
    }
    else {
        return maybeContent;
    }
}
exports.applyRowFn = applyRowFn;
exports.ASC = "ASC";
exports.DESC = "DESC";
exports.emptyCell = { content: () => (0, jsx_runtime_1.jsx)(jsx_runtime_1.Fragment, {}), value: "" };
function getIndentationCss(style, rowStyle, columnIndex, maybeContent) {
    // Look for cell-specific indent or row-specific indent (row-specific is only one the first column)
    const indent = (isGridCellContent(maybeContent) && maybeContent.indent) || (columnIndex === 0 && (rowStyle === null || rowStyle === void 0 ? void 0 : rowStyle.indent));
    if (typeof indent === "number" && style.levels !== undefined) {
        throw new Error("The indent param is deprecated for new beam fixed & flexible styles, use beamNestedFixedStyle or beamNestedFlexibleStyle");
    }
    return indent === 1 ? style.indentOneCss || {} : indent === 2 ? style.indentTwoCss || {} : {};
}
exports.getIndentationCss = getIndentationCss;
function getFirstOrLastCellCss(style, columnIndex, columns) {
    return {
        ...(columnIndex === 0 ? style.firstCellCss : {}),
        ...(columnIndex === columns.length - 1 ? style.lastCellCss : {}),
    };
}
exports.getFirstOrLastCellCss = getFirstOrLastCellCss;
/** A heuristic to detect the result of `React.createElement` / i.e. JSX. */
function isJSX(content) {
    return typeof content === "object" && content && "type" in content && "props" in content;
}
const alignmentToJustify = {
    left: "flex-start",
    center: "center",
    right: "flex-end",
};
const alignmentToTextAlign = {
    left: "left",
    center: "center",
    right: "right",
};
function getAlignment(column, maybeContent) {
    return (isGridCellContent(maybeContent) && maybeContent.alignment) || column.align || "left";
}
exports.getAlignment = getAlignment;
// For alignment, use: 1) cell def, else 2) column def, else 3) left.
function getJustification(column, maybeContent, as, alignment) {
    // Always apply text alignment.
    const textAlign = Css_1.Css.add("textAlign", alignmentToTextAlign[alignment]).$;
    if (as === "table") {
        return textAlign;
    }
    return { ...Css_1.Css.jc(alignmentToJustify[alignment]).$, ...textAlign };
}
exports.getJustification = getJustification;
/** Look at a row and get its filter value. */
function filterValue(value) {
    let maybeFn = value;
    if (value && typeof value === "object") {
        if ("value" in value) {
            maybeFn = value.value;
        }
        else if ("content" in value) {
            maybeFn = value.content;
        }
    }
    // Watch for functions that need to read from a potentially-changing proxy
    if (maybeFn instanceof Function) {
        return maybeFn();
    }
    return maybeFn;
}
function maybeApplyFunction(row, maybeFn) {
    return typeof maybeFn === "function" ? maybeFn(row) : maybeFn;
}
exports.maybeApplyFunction = maybeApplyFunction;
function matchesFilter(maybeContent, filter) {
    const value = filterValue(maybeContent);
    if (typeof value === "string") {
        return value.toLowerCase().includes(filter.toLowerCase());
    }
    else if (typeof value === "number") {
        return Number(filter) === value;
    }
    return false;
}
exports.matchesFilter = matchesFilter;
exports.HEADER = "header";
exports.TOTALS = "totals";
exports.EXPANDABLE_HEADER = "expandableHeader";
exports.reservedRowKinds = [exports.HEADER, exports.TOTALS, exports.EXPANDABLE_HEADER];
exports.zIndices = {
    stickyHeader: 4,
    stickyColumns: 3,
    expandableHeaderTitle: 2,
    expandableHeaderIcon: 1,
};
